import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InstagramIcon from '@mui/icons-material/Instagram';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import companylogo from '../images/companylogo.jpeg';
import './MainHeader.css';

const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';
const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';

const menuItems = [
  { text: 'Home', link: '/' },
  { text: 'About', link: '/about' },
  {
    text: 'Services',
    submenu: [
      { text: 'Equity', link: '/equity', Icon: AutoGraphIcon },
      { text: 'Derivatives', link: '/derivative', Icon: CandlestickChartIcon },
    ],
  },
  {
    text: 'Insights',
    submenu: [
      { text: 'Tracksheet', link: '/track-sheet', Icon: AssessmentIcon },
      { text: 'Complaint Status', link: '/complaint-status', Icon: SupportAgentIcon },
      { text: "FAQ's", link: '/faq', Icon: HelpOutlineIcon },
      { text: 'Disclosure with Advice', link: '/disclosures-advise', Icon: GavelIcon },
    ],
  },
  { text: 'Contact', link: '/contact' },
];

const socials = [
  { Icon: FacebookIcon, href: 'https://facebook.com', label: 'Facebook', color: '#1877f2' },
  { Icon: InstagramIcon, href: 'https://instagram.com', label: 'Instagram', color: '#e4405f' },
  { Icon: LinkedInIcon, href: 'https://linkedin.com', label: 'LinkedIn', color: '#0a66c2' },
  { Icon: WhatsAppIcon, href: 'https://wa.me/919510980382', label: 'WhatsApp', color: '#25d366' },
];

const MainHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [hoverMenu, setHoverMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimerRef = useRef(null);

  const openDropdown = (text) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHoverMenu(text);
  };

  const scheduleCloseDropdown = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setHoverMenu(null);
      closeTimerRef.current = null;
    }, 180);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      const h =
        document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min((y / h) * 100, 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  const isActive = (link) => location.pathname === link;
  const isParentActive = (submenu) =>
    submenu?.some((s) => location.pathname === s.link);

  const handleNav = (link) => {
    setDrawerOpen(false);
    navigate(link);
  };

  const headerBg = scrolled
    ? `linear-gradient(120deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)`
    : `linear-gradient(120deg, ${NAVY} 0%, #123a73 100%)`;

  const drawer = (
    <Box
      sx={{
        width: 300,
        height: '100%',
        background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)`,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,106,29,0.18) 0%, rgba(239,106,29,0) 70%)',
          pointerEvents: 'none',
        },
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src={companylogo}
          alt="Logo"
          onClick={() => handleNav('/')}
          sx={{
            height: 38,
            background: '#fff',
            borderRadius: 1,
            p: 0.5,
            cursor: 'pointer',
          }}
        />
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ flex: 1, px: 1, py: 1.5, position: 'relative', zIndex: 1 }}>
        {menuItems.map((item, index) =>
          item.submenu ? (
            <React.Fragment key={index}>
              <ListItemButton
                onClick={() =>
                  setOpenSubmenu(openSubmenu === item.text ? null : item.text)
                }
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  color: '#fff',
                  bgcolor: isParentActive(item.submenu)
                    ? 'rgba(239,106,29,0.15)'
                    : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                />
                {openSubmenu === item.text ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItemButton>
              <Collapse
                in={openSubmenu === item.text}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.submenu.map((sub, i) => {
                    const SubIcon = sub.Icon;
                    return (
                      <ListItemButton
                        key={i}
                        onClick={() => handleNav(sub.link)}
                        sx={{
                          pl: 3,
                          gap: 1.2,
                          borderRadius: 1.5,
                          mb: 0.25,
                          color: isActive(sub.link) ? ACCENT : '#cbd5e1',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.06)',
                            color: '#fff',
                          },
                        }}
                      >
                        {SubIcon && (
                          <SubIcon
                            sx={{
                              fontSize: '1rem',
                              color: isActive(sub.link) ? ACCENT : 'inherit',
                            }}
                          />
                        )}
                        <ListItemText
                          primary={sub.text}
                          primaryTypographyProps={{
                            fontSize: '0.92rem',
                            fontWeight: isActive(sub.link) ? 600 : 500,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          ) : (
            <ListItemButton
              key={index}
              onClick={() => handleNav(item.link)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                color: isActive(item.link) ? ACCENT : '#fff',
                bgcolor: isActive(item.link)
                  ? 'rgba(239,106,29,0.15)'
                  : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
              />
            </ListItemButton>
          )
        )}
      </List>

      {/* Socials row */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            fontSize: '0.7rem',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: 0.7,
            fontWeight: 600,
          }}
        >
          Follow us
        </Box>
        <Stack direction="row" spacing={1}>
          {socials.map(({ Icon, href, label, color }) => (
            <IconButton
              key={label}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              sx={{
                width: 36,
                height: 36,
                color: '#fff',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.25s ease',
                '& svg': { fontSize: '1rem' },
                '&:hover': {
                  background: color,
                  borderColor: color,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Icon />
            </IconButton>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 2 }} />
      <Box sx={{ p: 2, position: 'relative', zIndex: 1 }}>
        <Button
          fullWidth
          onClick={() => handleNav('/contact')}
          sx={{
            background: ACCENT,
            color: '#fff',
            fontWeight: 700,
            py: 1.1,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            boxShadow: '0 6px 16px rgba(239,106,29,0.35)',
            '&:hover': { background: ACCENT_HOVER },
          }}
        >
          Get Started
        </Button>
      </Box>
    </Box>
  );

  /* Top info strip — auto-hides on scroll */
  const topStripVisible = !scrolled;
  const topStripHeight = 36;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'blur(10px)',
          boxShadow: scrolled
            ? '0 8px 28px rgba(6,26,54,0.28)'
            : '0 2px 10px rgba(6,26,54,0.18)',
          transition: 'box-shadow 0.3s ease',
          borderBottom: scrolled
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid transparent',
        }}
      >
        {/* Scroll-progress bar (very top) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 2,
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
            transition: 'width 0.15s ease-out',
            zIndex: 3,
          }}
        />

        {/* Top info strip (desktop only) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            background: NAVY_DEEP,
            color: '#cbd5e1',
            overflow: 'hidden',
            maxHeight: topStripVisible ? topStripHeight : 0,
            opacity: topStripVisible ? 1 : 0,
            transition: 'max-height 0.35s ease, opacity 0.25s ease',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Box
            sx={{
              px: { md: 4 },
              height: topStripHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.78rem',
              fontWeight: 500,
            }}
          >
            <Stack direction="row" spacing={3} alignItems="center">
              <Box
                component="a"
                href="tel:+919510980382"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.7,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { color: ACCENT },
                }}
              >
                <PhoneIcon sx={{ fontSize: '0.9rem', color: ACCENT }} />
                +91 95109 80382
              </Box>
              <Box
                component="a"
                href="mailto:admin@realstockexperts.in"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.7,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { color: ACCENT },
                }}
              >
                <EmailIcon sx={{ fontSize: '0.9rem', color: ACCENT }} />
                admin@realstockexperts.in
              </Box>
              <Box
                sx={{
                  display: { md: 'none', lg: 'inline-flex' },
                  alignItems: 'center',
                  gap: 0.7,
                }}
              >
                <AccessTimeIcon sx={{ fontSize: '0.9rem', color: ACCENT }} />
                Mon – Sat · 9 AM – 6 PM
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.4} alignItems="center">
              {socials.map(({ Icon, href, label, color }) => (
                <IconButton
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  size="small"
                  sx={{
                    width: 26,
                    height: 26,
                    color: '#cbd5e1',
                    transition: 'all 0.2s ease',
                    '& svg': { fontSize: '0.95rem' },
                    '&:hover': {
                      color,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Icon />
                </IconButton>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Main bar */}
        <Box sx={{ background: headerBg, transition: 'background 0.3s ease' }}>
          <Toolbar
            sx={{
              minHeight: { xs: 64, md: scrolled ? 64 : 76 },
              transition: 'min-height 0.3s ease',
              px: { xs: 2, md: 4 },
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexShrink: 0,
                transition: 'all 0.3s ease',
                '&:hover img': {
                  filter:
                    'drop-shadow(0 6px 14px rgba(239,106,29,0.45))',
                  transform: 'scale(1.03)',
                },
              }}
            >
              <Box
                component="img"
                src={companylogo}
                alt="RealStock Experts"
                sx={{
                  height: { xs: 38, md: scrolled ? 40 : 46 },
                  background: '#fff',
                  borderRadius: 1.2,
                  p: 0.6,
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>

            {/* Desktop nav */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {menuItems.map((item, index) => {
                  const active = item.submenu
                    ? isParentActive(item.submenu)
                    : isActive(item.link);
                  const isOpen = hoverMenu === item.text;

                  if (item.submenu) {
                    return (
                      <Box
                        key={index}
                        onMouseEnter={() => openDropdown(item.text)}
                        onMouseLeave={scheduleCloseDropdown}
                        sx={{ position: 'relative' }}
                      >
                        <Button
                          disableRipple
                          endIcon={
                            <KeyboardArrowDownIcon
                              sx={{
                                transition: 'transform 0.25s ease',
                                transform: isOpen
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                              }}
                            />
                          }
                          sx={{
                            color: active ? ACCENT : '#fff',
                            fontWeight: 600,
                            fontSize: '0.98rem',
                            textTransform: 'none',
                            px: 1.8,
                            py: 1,
                            borderRadius: 1.5,
                            position: 'relative',
                            letterSpacing: 0.2,
                            transition: 'color 0.25s ease',
                            '&:hover': {
                              background: 'rgba(255,255,255,0.08)',
                              color: ACCENT,
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              left: '20%',
                              right: '20%',
                              bottom: 6,
                              height: '2px',
                              background: `linear-gradient(90deg, ${ACCENT}, #f59e0b)`,
                              borderRadius: 2,
                              transform: active ? 'scaleX(1)' : 'scaleX(0)',
                              transition: 'transform 0.25s ease',
                              transformOrigin: 'center',
                            },
                            '&:hover::after': { transform: 'scaleX(1)' },
                          }}
                        >
                          {item.text}
                        </Button>

                        {/* Dropdown */}
                        <Box
                          onMouseEnter={() => openDropdown(item.text)}
                          onMouseLeave={scheduleCloseDropdown}
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: isOpen
                              ? 'translateX(-50%) translateY(0)'
                              : 'translateX(-50%) translateY(8px)',
                            opacity: isOpen ? 1 : 0,
                            pointerEvents: isOpen ? 'auto' : 'none',
                            transition: 'all 0.22s ease',
                            minWidth: 240,
                            pt: 1.5,
                            zIndex: theme.zIndex.appBar + 1,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: '#fff',
                              color: '#0b2447',
                              borderRadius: 2,
                              boxShadow: '0 24px 50px rgba(6,26,54,0.22)',
                              overflow: 'hidden',
                              position: 'relative',
                              border: '1px solid rgba(11,36,71,0.06)',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -6,
                                left: '50%',
                                transform:
                                  'translateX(-50%) rotate(45deg)',
                                width: 12,
                                height: 12,
                                bgcolor: '#fff',
                                boxShadow:
                                  '-2px -2px 4px rgba(6,26,54,0.04)',
                              },
                              /* Top color edge */
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 2,
                                background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                              },
                            }}
                          >
                            <List sx={{ py: 0.8 }}>
                              {item.submenu.map((sub, i) => {
                                const subActive = isActive(sub.link);
                                const SubIcon = sub.Icon;
                                return (
                                  <ListItemButton
                                    key={i}
                                    component={Link}
                                    to={sub.link}
                                    onClick={() => setHoverMenu(null)}
                                    sx={{
                                      py: 1.1,
                                      px: 2,
                                      gap: 1.2,
                                      color: subActive ? ACCENT : '#0b2447',
                                      fontWeight: subActive ? 700 : 500,
                                      borderLeft: subActive
                                        ? `3px solid ${ACCENT}`
                                        : '3px solid transparent',
                                      transition:
                                        'all 0.2s ease',
                                      '&:hover': {
                                        bgcolor: 'rgba(239,106,29,0.06)',
                                        color: ACCENT,
                                        borderLeft: `3px solid ${ACCENT}`,
                                        pl: 2.3,
                                      },
                                      '&:hover .sub-icon': {
                                        background:
                                          'rgba(239,106,29,0.18)',
                                      },
                                    }}
                                  >
                                    {SubIcon && (
                                      <Box
                                        className="sub-icon"
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          borderRadius: 1,
                                          background:
                                            'rgba(239,106,29,0.08)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: ACCENT,
                                          transition:
                                            'background 0.2s ease',
                                          '& svg': {
                                            fontSize: '0.95rem',
                                          },
                                        }}
                                      >
                                        <SubIcon />
                                      </Box>
                                    )}
                                    <ListItemText
                                      primary={sub.text}
                                      primaryTypographyProps={{
                                        fontSize: '0.92rem',
                                        fontWeight: 'inherit',
                                      }}
                                    />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          </Box>
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    <Button
                      key={index}
                      component={Link}
                      to={item.link}
                      disableRipple
                      sx={{
                        color: active ? ACCENT : '#fff',
                        fontWeight: 600,
                        fontSize: '0.98rem',
                        textTransform: 'none',
                        px: 1.8,
                        py: 1,
                        borderRadius: 1.5,
                        position: 'relative',
                        letterSpacing: 0.2,
                        transition: 'color 0.25s ease',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.08)',
                          color: ACCENT,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: '20%',
                          right: '20%',
                          bottom: 6,
                          height: '2px',
                          background: `linear-gradient(90deg, ${ACCENT}, #f59e0b)`,
                          borderRadius: 2,
                          transform: active ? 'scaleX(1)' : 'scaleX(0)',
                          transition: 'transform 0.25s ease',
                          transformOrigin: 'center',
                        },
                        '&:hover::after': { transform: 'scaleX(1)' },
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Right side: CTA on desktop, menu icon on mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <Button
                  component={Link}
                  to="/contact"
                  sx={{
                    ml: 1,
                    background: `linear-gradient(135deg, ${ACCENT} 0%, #f59e0b 100%)`,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.6,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    boxShadow: '0 8px 18px rgba(239,106,29,0.4)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '50%',
                      height: '100%',
                      background:
                        'linear-gradient(120deg, transparent, rgba(255,255,255,0.3), transparent)',
                      transition: 'left 0.6s ease',
                    },
                    '&:hover': {
                      background: `linear-gradient(135deg, ${ACCENT_HOVER} 0%, #ea8b07 100%)`,
                      boxShadow: '0 12px 24px rgba(239,106,29,0.5)',
                      transform: 'translateY(-2px)',
                    },
                    '&:hover::before': { left: '150%' },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Get Started
                </Button>
              )}

              {isMobile && (
                <IconButton
                  edge="end"
                  aria-label="open menu"
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 1.5,
                    p: 1,
                    '&:hover': {
                      background: 'rgba(255,255,255,0.08)',
                      borderColor: ACCENT,
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      {/* Spacer so page content doesn't slide under the fixed header */}
      <Box
        sx={{
          height: {
            xs: 64,
            md: scrolled ? 64 : 76 + topStripHeight,
          },
          transition: 'height 0.35s ease',
        }}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { boxShadow: '-10px 0 30px rgba(0,0,0,0.25)' } }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default MainHeader;
